/**
 * Location Selector Component
 *
 * Dropdown menu for selecting province and city.
 * Shows "Coming Soon" badge for unsupported locations.
 */

import { useState, useRef, useEffect } from "react";
import { MapPin, ChevronDown, Check, Clock } from "lucide-react";
import { useLocation } from "../contexts/LocationContext";

interface LocationSelectorProps {
  variant?: "header" | "compact" | "full";
  className?: string;
}

export function LocationSelector({
  variant = "header",
  className = "",
}: LocationSelectorProps) {
  const {
    province,
    city,
    provinceId,
    cityId,
    setLocation,
    isSupported,
    allProvinces,
    locationLabel,
  } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedProvinceId, setSelectedProvinceId] = useState(provinceId);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset selected province when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSelectedProvinceId(provinceId);
    }
  }, [isOpen, provinceId]);

  const selectedProvince = allProvinces.find(
    (p) => p.id === selectedProvinceId,
  );

  const handleProvinceSelect = (pId: string) => {
    setSelectedProvinceId(pId);
  };

  const handleCitySelect = (cId: string) => {
    setLocation(selectedProvinceId, cId);
    setIsOpen(false);
  };

  if (variant === "compact") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <MapPin className="w-4 h-4" />
          <span>{city?.name || province.abbreviation}</span>
          <ChevronDown
            className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <DropdownMenu
            allProvinces={allProvinces}
            selectedProvinceId={selectedProvinceId}
            currentProvinceId={provinceId}
            currentCityId={cityId}
            onProvinceSelect={handleProvinceSelect}
            onCitySelect={handleCitySelect}
            selectedProvince={selectedProvince}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
          ${
            isSupported
              ? "border-gray-200 hover:border-gray-300 bg-white"
              : "border-amber-200 bg-amber-50 hover:border-amber-300"
          }
        `}
      >
        <MapPin
          className={`w-4 h-4 ${isSupported ? "text-blue-600" : "text-amber-600"}`}
        />
        <span className="text-sm font-medium">{locationLabel}</span>
        {!isSupported && (
          <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
            Coming Soon
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <DropdownMenu
          allProvinces={allProvinces}
          selectedProvinceId={selectedProvinceId}
          currentProvinceId={provinceId}
          currentCityId={cityId}
          onProvinceSelect={handleProvinceSelect}
          onCitySelect={handleCitySelect}
          selectedProvince={selectedProvince}
        />
      )}
    </div>
  );
}

interface DropdownMenuProps {
  allProvinces: typeof import("../config/locations").PROVINCES;
  selectedProvinceId: string;
  currentProvinceId: string;
  currentCityId: string | null;
  onProvinceSelect: (id: string) => void;
  onCitySelect: (id: string) => void;
  selectedProvince:
    | (typeof import("../config/locations").PROVINCES)[0]
    | undefined;
}

function DropdownMenu({
  allProvinces,
  selectedProvinceId,
  currentProvinceId,
  currentCityId,
  onProvinceSelect,
  onCitySelect,
  selectedProvince,
}: DropdownMenuProps) {
  return (
    <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Select Location</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Choose your province and city for localized information
        </p>
      </div>

      <div className="flex divide-x divide-gray-200">
        {/* Province List */}
        <div className="w-1/2 max-h-64 overflow-y-auto">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            Province
          </div>
          {allProvinces.map((prov) => (
            <button
              key={prov.id}
              onClick={() => onProvinceSelect(prov.id)}
              className={`
                w-full px-3 py-2 text-left text-sm flex items-center justify-between
                transition-colors
                ${
                  selectedProvinceId === prov.id
                    ? "bg-blue-50 text-blue-700"
                    : "hover:bg-gray-50 text-gray-700"
                }
              `}
            >
              <span className="flex items-center gap-2">
                {prov.name}
                {!prov.isSupported && (
                  <Clock className="w-3 h-3 text-amber-500" />
                )}
              </span>
              {currentProvinceId === prov.id && (
                <Check className="w-4 h-4 text-blue-600" />
              )}
            </button>
          ))}
        </div>

        {/* City List */}
        <div className="w-1/2 max-h-64 overflow-y-auto">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            City
          </div>
          {selectedProvince?.cities.map((c) => (
            <button
              key={c.id}
              onClick={() => onCitySelect(c.id)}
              className={`
                w-full px-3 py-2 text-left text-sm flex items-center justify-between
                transition-colors
                ${
                  currentProvinceId === selectedProvinceId &&
                  currentCityId === c.id
                    ? "bg-blue-50 text-blue-700"
                    : "hover:bg-gray-50 text-gray-700"
                }
              `}
            >
              <span className="flex items-center gap-2">
                {c.name}
                {!c.isSupported && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-600 rounded">
                    Soon
                  </span>
                )}
              </span>
              {currentProvinceId === selectedProvinceId &&
                currentCityId === c.id && (
                  <Check className="w-4 h-4 text-blue-600" />
                )}
            </button>
          ))}
        </div>
      </div>

      {/* Footer with info */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600">
            <span className="font-medium">Work in Progress:</span> We're
            expanding across Canada! Currently, only Alberta (Calgary) has full
            support. Other provinces and cities are coming soon.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Alert banner shown when user selects an unsupported location
 */
export function LocationNotSupportedBanner() {
  const { isSupported, locationLabel, setLocation } = useLocation();

  if (isSupported) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {locationLabel} is coming soon!
            </p>
            <p className="text-xs text-amber-600">
              We're working on adding support for this location. For now, you
              can explore with Calgary, AB data.
            </p>
          </div>
        </div>
        <button
          onClick={() => setLocation("ab", "calgary")}
          className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors whitespace-nowrap"
        >
          Use Calgary, AB
        </button>
      </div>
    </div>
  );
}

export default LocationSelector;
